import postalCodes from 'postal-codes-js';
import { Passenger, PassengerError } from 'interfaces/booking';
import moment from 'moment';
import { getMomentFromPassenger, validateEmail } from 'utils/utils';

export const validateInvoicePassengerInput = (
  inv: Passenger,
  keyOfpassenger: keyof Passenger,
  typeOfPassenger: string,
): PassengerError => {
  const error = {} as PassengerError;

  switch (keyOfpassenger) {
    case 'email':
      // validate email
      if (!inv.email) {
        error.email = 'Enter email address.';
      } else if (!validateEmail(inv.email)) {
        error.email = 'Enter valid email address.';
      } else {
        error.email = '';
      }
      break;
    case 'firstName':
      // validate firstName
      if (!inv.firstName) {
        error.firstName = 'Enter first name.';
      } else if (inv.firstName.length > 100) {
        error.firstName = 'First name too long.';
      } else {
        error.firstName = '';
      }
      break;
    case 'lastName':
      // validate lastName
      if (!inv.lastName) {
        error.lastName = 'Enter last name.';
      } else if (inv.lastName.length > 100) {
        error.lastName = 'Last name too long.';
      } else {
        error.lastName = '';
      }
      break;
    case 'address1':
      // validate address line 1
      if (!inv.address1) {
        error.address1 = 'Enter address.';
      } else if (inv.address1.length > 100) {
        error.address1 = 'Address too long.';
      } else {
        error.address1 = '';
      }
      break;
    case 'city':
      // validate city
      if (!inv.city) {
        error.city = 'Enter city.';
      } else if (inv.city.length > 100) {
        error.city = 'City name too long.';
      } else {
        error.city = '';
      }
      break;
    case 'countryCode':
      // validate country code
      if (inv?.countryCode?.name === '') {
        error.countryCode = 'Select country.';
      } else {
        error.countryCode = '';
      }
      break;
    case 'namePrefix':
      // validate gender
      if (!inv.namePrefix || !['Mr', 'Mrs', 'Ms'].includes(inv.namePrefix)) {
        error.namePrefix = 'Selected gender.';
      } else {
        error.namePrefix = '';
      }
      break;
    case 'postalCode':
      // validate postal code
      if (!inv.postalCode) {
        error.postalCode = 'Enter zip/postal code.';
      } else if (!/^[a-zA-Z0-9- ]+$/.test(inv.postalCode)) {
        error.postalCode = 'Invalid zip/postal code format.';
      } else if (inv.postalCode.length > 20) {
        error.postalCode = 'Zip/postal code too long.';
      } else if (
        inv.countryCode &&
        inv.countryCode.alpha2Code &&
        !postalCodes.validate(inv.countryCode.alpha2Code, inv.postalCode)
      ) {
        error.postalCode = 'Enter valid zip/postal code.';
      } else {
        error.postalCode = '';
      }
      break;
    case 'phoneNumber':
      // validate phone number
      const numberWithoutSpaces = inv.phoneNumber.formattedValue.replace(
        /\s/g,
        '',
      );
      if (!inv.phoneNumber.formattedValue || !inv.phoneNumber.value) {
        error.phoneNumber = 'Enter phone number.';
      } else if (
        !/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/.test(
          numberWithoutSpaces,
        )
      ) {
        error.phoneNumber = 'Invalid phone number.';
      } else {
        error.phoneNumber = '';
      }
      break;
    case 'birthYear':
      // validate birth year
      if (!inv.birthYear) {
        error.birthYear = 'Enter birthdate.';
      } else {
        error.birthYear = '';
      }
      break;
    case 'birthMonth':
      // validate birth month
      if (!inv.birthMonth) {
        error.birthMonth = 'Enter birthdate.';
      } else if (
        inv.birthMonth &&
        (inv.birthMonth < 1 || inv.birthMonth > 12)
      ) {
        error.birthMonth = 'Enter a valid month.';
      } else {
        error.birthMonth = '';
      }
      break;
    case 'birthDay':
      // validate birth day
      if (!inv.birthDay) {
        error.birthDay = 'Enter birthdate.';
      } else if (inv.birthDay && (inv.birthDay < 1 || inv.birthDay > 31)) {
        error.birthDay = 'Enter a valid day.';
      } else {
        error.birthDay = '';
      }
      break;
    case 'documentId':
      if (!inv.documentId) {
        error.documentId = 'Enter document ID.';
      } else {
        error.documentId = '';
      }
      break;
    case 'documentCountry':
      if (!inv.documentCountry || inv.documentCountry.name === '') {
        error.documentCountry = 'Enter issuer country of the document.';
      } else {
        error.documentCountry = '';
      }
      break;
    case 'documentExpirationDay':
      if (!inv.documentExpirationDay) {
        error.documentExpirationDay = 'Enter day.';
      } else if (
        inv.documentExpirationDay &&
        (inv.documentExpirationDay < 1 || inv.documentExpirationDay > 31)
      ) {
        error.documentExpirationDay = 'Enter a valid day.';
      } else {
        error.documentExpirationDay = '';
      }
      break;
    case 'documentExpirationMonth':
      if (!inv.documentExpirationMonth) {
        error.documentExpirationMonth = 'Enter month.';
      } else if (
        inv.documentExpirationMonth &&
        (inv.documentExpirationMonth < 1 || inv.documentExpirationMonth > 12)
      ) {
        error.documentExpirationMonth = 'Enter a valid month.';
      } else {
        error.documentExpirationMonth = '';
      }
      break;
    case 'documentExpirationYear':
      const currentYear = new Date().getFullYear();
      if (!inv.documentExpirationYear) {
        error.documentExpirationYear = 'Enter year.';
      } else if (
        inv.documentExpirationYear &&
        inv.documentExpirationYear < currentYear
      ) {
        error.documentExpirationYear = 'Expired year.';
      } else if (inv.documentExpirationYear.toString().length !== 4) {
        error.documentExpirationYear = 'Enter a valid year.';
      } else {
        error.documentExpirationYear = '';
      }
      break;
    default:
      break;
  }

  // validate birth date
  const parsedBirthDate = getMomentFromPassenger(inv);
  if (parsedBirthDate) {
    const age = moment().diff(parsedBirthDate, 'years');
    // check value
    switch (typeOfPassenger) {
      case 'adults':
        if (age < 12) {
          error.birthDate = 'Enter valid birthdate (must be over 11).';
        }
        break;
      case 'children':
        if (!(age >= 0 && age <= 12)) {
          error.birthDate = 'Enter valid birthdate (must be 0-12).';
        }
        break;
      case 'infants':
        if (!(age >= 0 && age <= 2)) {
          error.birthDate = 'Enter valid birthdate (must be under 3).';
        }
        break;
    }
  }
  return error;
};
